import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import Tesseract from 'tesseract.js';
import Fuse from 'fuse.js';

// Enhanced file parser for multiple formats
export class EnhancedFileParser {
  constructor() {
    this.supportedFormats = {
      text: ['.txt', '.csv', '.tsv'],
      office: ['.docx', '.xlsx', '.xls'],
      pdf: ['.pdf'],
      image: ['.jpg', '.jpeg', '.png', '.bmp', '.tiff', '.webp']
    };

    // Common field synonyms for fuzzy matching
    this.fieldSynonyms = {
      'well_name': ['well name', 'wellname', 'well no', 'well number', 'well id', 'well identifier'],
      'operator': ['operator', 'company', 'operating company', 'op'],
      'rig': ['rig', 'rig name', 'rig number', 'drilling rig'],
      'depth': ['depth', 'well depth', 'total depth', 'td', 'measured depth', 'md'],
      'date': ['date', 'report date', 'drilling date', 'spud date'],
      'field': ['field', 'field name', 'oil field', 'gas field'],
      'location': ['location', 'well location', 'coordinates', 'lat/lon'],
      'formation': ['formation', 'geological formation', 'target formation'],
      'mud_weight': ['mud weight', 'drilling mud weight', 'mud density'],
      'flow_rate': ['flow rate', 'pump rate', 'circulation rate', 'gpm'],
      'pressure': ['pressure', 'drilling pressure', 'pump pressure', 'psi'],
      'temperature': ['temperature', 'bottom hole temperature', 'bht'],
      'remarks': ['remarks', 'comments', 'notes', 'observations'],
      'shift': ['shift', 'tour', 'crew shift', 'daylight tour', 'evening tour', 'night tour']
    };
  }

  // Main entry point for parsing files
  async parseFile(file, formFields = []) {
    try {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const fileType = this.getFileType(fileExtension);
      
      let textContent = '';
      
      switch (fileType) {
        case 'text':
          textContent = await this.parseTextFile(file);
          break;
        case 'office':
          textContent = await this.parseOfficeFile(file, fileExtension);
          break;
        case 'pdf':
          textContent = await this.parsePdfFile(file);
          break;
        case 'image':
          textContent = await this.parseImageFile(file);
          break;
        default:
          throw new Error(`Unsupported file format: ${fileExtension}`);
      }

      // Extract field-value pairs from text content
      const extractedData = this.extractFieldValuePairs(textContent);
      
      // Map to form fields using fuzzy matching
      const mappings = this.mapToFormFields(extractedData, formFields);
      
      let processingInfo = undefined;
      
      if (fileType === 'office' && file.name.match(/\.(xlsx|xls)$/i)) {
        processingInfo = `Processed ${Object.keys(extractedData).length} field-value pairs from Excel file`;
      } else if (fileType === 'text' && file.name.match(/\.csv$/i)) {
        processingInfo = `Processed ${Object.keys(extractedData).length} field-value pairs from CSV file`;
      }

      return {
        type: 'enhanced',
        fileType,
        textContent,
        extractedData,
        mappings: mappings.sort((a, b) => b.confidence - a.confidence),
        processingInfo
      };
    } catch (error) {
      console.error('File parsing error:', error);
      throw new Error(`Failed to parse file: ${error.message}`);
    }
  }

  // Determine file type category
  getFileType(extension) {
    for (const [type, extensions] of Object.entries(this.supportedFormats)) {
      if (extensions.includes(extension)) {
        return type;
      }
    }
    return 'unknown';
  }

  // Parse text files (CSV, TSV, TXT)
  async parseTextFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        
        // For CSV files, convert to colon-separated format for better extraction
        if (fileExtension === '.csv') {
          try {
            const convertedContent = this.convertCSVToKeyValue(content);
            resolve(convertedContent);
          } catch (error) {
            resolve(content); // Fall back to raw content if conversion fails
          }
        } else {
          resolve(content);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read text file'));
      reader.readAsText(file);
    });
  }

  // Convert CSV content to key-value format for better extraction
  convertCSVToKeyValue(content) {
    const lines = content.trim().split('\n');
    
    if (lines.length < 2) {
      return content; // Return original if not enough lines
    }
    
    const headers = this.parseCSVLine(lines[0]);
    
    // Check if this is a 2-column field-value format
    if (headers.length === 2) {
      const firstHeader = headers[0].toLowerCase();
      const secondHeader = headers[1].toLowerCase();
      
      // Check if headers suggest field-value format
      if ((firstHeader.includes('field') || firstHeader.includes('label') || firstHeader.includes('name')) &&
          (secondHeader.includes('value') || secondHeader.includes('data'))) {
        
        let convertedContent = '';
        
        // Convert each row to "Label: Value" format
        for (let i = 1; i < lines.length; i++) {
          const rowData = this.parseCSVLine(lines[i]);
          if (rowData.length >= 2 && rowData[0] && rowData[1]) {
            const field = rowData[0].trim();
            const value = rowData[1].trim();
            if (field && value && value !== '-' && value !== 'N/A') {
              convertedContent += `${field}: ${value}\n`;
            }
          }
        }
        
        return convertedContent;
      }
    }
    
    // For other CSV formats, try to detect patterns
    let convertedContent = '';
    const firstRow = this.parseCSVLine(lines[1]);
    
    // If first row after headers has data, try horizontal pairing
    if (firstRow.length >= 2) {
      for (let i = 1; i < lines.length; i++) {
        const rowData = this.parseCSVLine(lines[i]);
        for (let j = 0; j < rowData.length - 1; j += 2) {
          if (rowData[j] && rowData[j + 1]) {
            const field = rowData[j].trim();
            const value = rowData[j + 1].trim();
            if (field && value && value !== '-' && value !== 'N/A') {
              convertedContent += `${field}: ${value}\n`;
            }
          }
        }
      }
    }
    
    return convertedContent || content; // Fall back to original if no conversion worked
  }

  // Helper method to properly parse CSV lines with quoted values
  parseCSVLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result.map(item => item.replace(/^"(.*)"$/, '$1')); // Remove surrounding quotes
  }

  // Parse Office documents (Word, Excel)
  async parseOfficeFile(file, extension) {
    if (extension === '.docx') {
      return this.parseWordDocument(file);
    } else if (['.xlsx', '.xls'].includes(extension)) {
      return this.parseExcelDocument(file);
    }
    throw new Error(`Unsupported office format: ${extension}`);
  }

  // Parse Word documents using mammoth
  async parseWordDocument(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      throw new Error(`Failed to parse Word document: ${error.message}`);
    }
  }

  // Parse Excel documents using xlsx
  async parseExcelDocument(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      let extractedPairs = {};
      
      // Process all sheets
      workbook.SheetNames.forEach(sheetName => {
        const sheet = workbook.Sheets[sheetName];
        const sheetPairs = this.extractExcelCellPairs(sheet, sheetName);
        
        // Merge pairs from this sheet
        Object.assign(extractedPairs, sheetPairs);
      });
      
      // Convert to text format for compatibility with existing extraction logic
      let textContent = '';
      Object.entries(extractedPairs).forEach(([label, value]) => {
        textContent += `${label}: ${value}\n`;
      });
      
      return textContent;
    } catch (error) {
      throw new Error(`Failed to parse Excel document: ${error.message}`);
    }
  }

  // Extract label-value pairs from Excel sheet cells
  extractExcelCellPairs(sheet, sheetName) {
    const pairs = {};
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    
    // Get all cell values as a 2D array
    const sheetData = [];
    for (let row = range.s.r; row <= range.e.r; row++) {
      const rowData = [];
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cellValue = sheet[cellAddress] ? sheet[cellAddress].v : '';
        rowData.push(String(cellValue || '').trim());
      }
      sheetData.push(rowData);
    }
    
    // Try different patterns to extract label-value pairs
    
    // Pattern 1: Horizontal pairs (A1=label, B1=value, A2=label, B2=value)
    for (let row = 0; row < sheetData.length; row++) {
      if (sheetData[row].length >= 2) {
        const label = sheetData[row][0];
        const value = sheetData[row][1];
        
        if (label && value && 
            label !== value && 
            !this.isNumericOnly(label) &&  // Labels shouldn't be just numbers
            value !== '-' && value !== 'N/A' && value !== '') {
          
          // Clean up the label (remove colons, extra spaces)
          const cleanLabel = label.replace(/[:=]?\s*$/, '').trim();
          if (cleanLabel) {
            const key = sheetName.length > 1 ? `${sheetName}_${cleanLabel}` : cleanLabel;
            pairs[key] = value;
          }
        }
      }
    }
    
    // Pattern 2: Vertical pairs (A1=label, A2=value, B1=label, B2=value)
    if (Object.keys(pairs).length < 3) { // If horizontal didn't find much, try vertical
      const maxCols = Math.min(10, Math.max(...sheetData.map(row => row.length))); // Limit to 10 columns
      
      for (let col = 0; col < maxCols; col++) {
        for (let row = 0; row < sheetData.length - 1; row++) {
          if (sheetData[row] && sheetData[row + 1]) {
            const label = sheetData[row][col];
            const value = sheetData[row + 1][col];
            
            if (label && value && 
                label !== value && 
                !this.isNumericOnly(label) &&
                value !== '-' && value !== 'N/A' && value !== '') {
              
              const cleanLabel = label.replace(/[:=]?\s*$/, '').trim();
              if (cleanLabel) {
                const key = sheetName.length > 1 ? `${sheetName}_${cleanLabel}` : cleanLabel;
                pairs[key] = value;
              }
            }
          }
        }
      }
    }
    
    // Pattern 3: Key-value table detection (look for columns that might be headers)
    if (Object.keys(pairs).length < 3 && sheetData.length > 0) {
      // Check if first row looks like headers
      const firstRow = sheetData[0];
      const possibleHeaders = ['field', 'label', 'name', 'key', 'parameter', 'property'];
      const valueHeaders = ['value', 'data', 'result', 'amount', 'quantity'];
      
      let labelColIndex = -1;
      let valueColIndex = -1;
      
      // Find label and value columns
      firstRow.forEach((header, index) => {
        const headerLower = header.toLowerCase();
        if (possibleHeaders.some(h => headerLower.includes(h))) {
          labelColIndex = index;
        }
        if (valueHeaders.some(h => headerLower.includes(h))) {
          valueColIndex = index;
        }
      });
      
      // If we found both label and value columns, extract data
      if (labelColIndex >= 0 && valueColIndex >= 0) {
        for (let row = 1; row < sheetData.length; row++) {
          if (sheetData[row][labelColIndex] && sheetData[row][valueColIndex]) {
            const label = String(sheetData[row][labelColIndex]).trim();
            const value = String(sheetData[row][valueColIndex]).trim();
            
            if (label && value && value !== '-' && value !== 'N/A') {
              const key = sheetName.length > 1 ? `${sheetName}_${label}` : label;
              pairs[key] = value;
            }
          }
        }
      }
    }
    
    return pairs;
  }

  // Helper function to check if a string is numeric only
  isNumericOnly(str) {
    return /^\d+\.?\d*$/.test(str.trim());
  }

  // Parse PDF files using backend API
  async parsePdfFile(file) {
    try {
      // Use backend API for PDF parsing
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/file-parser/parse', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'PDF parsing failed');
      }

      const result = await response.json();
      return result.textContent;
    } catch (error) {
      throw new Error(`Failed to parse PDF: ${error.message}`);
    }
  }

  // Parse image files using OCR
  async parseImageFile(file) {
    try {
      const { data: { text } } = await Tesseract.recognize(file, 'eng', {
        logger: m => console.log('OCR Progress:', m)
      });
      return text;
    } catch (error) {
      throw new Error(`Failed to perform OCR on image: ${error.message}`);
    }
  }

  // Extract field-value pairs from text content
  extractFieldValuePairs(textContent) {
    const pairs = {};
    const lines = textContent.split('\n').map(line => line.trim()).filter(line => line);
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Method 1: Look for colon-separated pairs (Label: Value)
      const colonMatch = line.match(/^([^:]+):\s*(.+)$/);
      if (colonMatch) {
        const label = colonMatch[1].trim();
        const value = colonMatch[2].trim();
        if (value && value !== '-' && value !== 'N/A') {
          pairs[label] = value;
        }
        continue;
      }
      
      // Method 2: Look for equals-separated pairs (Label = Value)
      const equalsMatch = line.match(/^([^=]+)=\s*(.+)$/);
      if (equalsMatch) {
        const label = equalsMatch[1].trim();
        const value = equalsMatch[2].trim();
        if (value && value !== '-' && value !== 'N/A') {
          pairs[label] = value;
        }
        continue;
      }
      
      // Method 3: Look for tab-separated pairs
      const tabParts = line.split('\t');
      if (tabParts.length === 2) {
        const label = tabParts[0].trim();
        const value = tabParts[1].trim();
        if (label && value && value !== '-' && value !== 'N/A') {
          pairs[label] = value;
        }
        continue;
      }
      
      // Method 4: Look for space-separated pairs (minimum 3 spaces between label and value)
      const spaceMatch = line.match(/^([^:=]+?)\s{3,}(.+)$/);
      if (spaceMatch) {
        const label = spaceMatch[1].trim();
        const value = spaceMatch[2].trim();
        if (value && value !== '-' && value !== 'N/A') {
          pairs[label] = value;
        }
        continue;
      }
      
      // Method 5: Look for labels and check next line for value
      if (i < lines.length - 1) {
        const nextLine = lines[i + 1];
        
        // If current line looks like a label (ends with : or is short and next line is longer)
        if (line.endsWith(':') || (line.length < 30 && nextLine.length > line.length)) {
          const label = line.replace(/:\s*$/, '').trim();
          const value = nextLine.trim();
          
          // Check if next line looks like a value (not another label)
          if (value && !value.includes(':') && !value.includes('=') && value !== '-' && value !== 'N/A') {
            pairs[label] = value;
            i++; // Skip the next line since we used it as a value
          }
        }
      }
    }
    
    return pairs;
  }

  // Map extracted data to form fields using fuzzy matching
  mapToFormFields(extractedData, formFields) {
    const mappings = [];
    
    // Create searchable form fields array
    const searchableFields = formFields.map(field => ({
      name: field.name,
      label: field.label || field.name,
      searchText: `${field.name} ${field.label || ''} ${this.getFieldSynonyms(field.name).join(' ')}`.toLowerCase()
    }));

    // For each extracted field, find the best matching form field
    Object.entries(extractedData).forEach(([extractedLabel, value]) => {
      const bestMatch = this.findBestFieldMatch(extractedLabel, searchableFields);
      
      if (bestMatch && bestMatch.confidence > 0.4) { // Minimum 40% confidence
        mappings.push({
          fileField: extractedLabel,
          formField: bestMatch.name,
          formLabel: bestMatch.label,
          value: value,
          confidence: bestMatch.confidence,
          selected: bestMatch.confidence > 0.6 // Auto-select high confidence matches
        });
      }
    });

    return mappings;
  }

  // Find the best matching form field for an extracted label
  findBestFieldMatch(extractedLabel, formFields) {
    const options = {
      keys: ['name', 'label', 'searchText'],
      threshold: 0.6, // Lower threshold = more strict matching
      includeScore: true
    };

    const fuse = new Fuse(formFields, options);
    const results = fuse.search(extractedLabel.toLowerCase());

    if (results.length > 0) {
      const match = results[0];
      return {
        ...match.item,
        confidence: 1 - match.score // Convert fuse.js score to confidence (lower score = better match)
      };
    }

    return null;
  }

  // Get synonyms for a field name
  getFieldSynonyms(fieldName) {
    const normalizedFieldName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    for (const [key, synonyms] of Object.entries(this.fieldSynonyms)) {
      if (normalizedFieldName.includes(key) || key.includes(normalizedFieldName)) {
        return synonyms;
      }
    }
    
    return [];
  }

  // Check if file format is supported
  isSupported(fileName) {
    const extension = '.' + fileName.split('.').pop().toLowerCase();
    return Object.values(this.supportedFormats).flat().includes(extension);
  }

  // Get supported file extensions as a flat array
  getSupportedExtensions() {
    return Object.values(this.supportedFormats).flat();
  }
}

// Export singleton instance
export const fileParser = new EnhancedFileParser();